import { Injectable, Injector } from '@angular/core';
import { ApiClientService } from '@core/services/api-client.service';
import { NgxIndexedDBService } from 'ngx-indexed-db';

import { LocalStorage } from 'ngx-webstorage';

import { firstValueFrom, Observable } from 'rxjs';
// import { NavService } from './nav.service';


@Injectable({
  providedIn: 'root' // This should be present
})
export class PermissionService {
  @LocalStorage()
  private userDetails;

  /**
   * Deduplication: all concurrent callers share the same in-flight Promise.
   * Resets to null when the request completes (success or error).
   */
  private _updateInFlight: Promise<void> | null = null;

  /**
   * In-memory cache for getResourceCategoryById — avoids repeated IndexDB reads
   * within the same page session (e.g. layout + profile + header all reading
   * CUSTOMER_DASHBOARD on init). Cleared by invalidatePermissions().
   */
  private _categoryCache = new Map<string, any>();

  /**
   * Session key — set once per user+company combination after a successful
   * API fetch. Prevents repeated API calls within the same page session.
   * Cleared by invalidatePermissions() on company switch or logout.
   */
  private _sessionKey: string | null = null;
//   private navService: NavService;
  constructor(private dbService: NgxIndexedDBService, private http: ApiClientService,private injector: Injector) { }
 getCurrentUserId(): string {
  const currentCompany = localStorage.getItem('currentCompany') || 'unity';
  const userDetailsString =
    localStorage.getItem(`tibss.${currentCompany}-userDetails`) ||
    localStorage.getItem('userDetails'); // fallback for backward compatibility
  try {
    const userDetails = JSON.parse(userDetailsString || '{}');
    return userDetails.userID?.toString() || 'default-user';
  } catch (error) {
    console.error('Error parsing userDetails from localStorage:', error);
    return 'default-user';
  }
}

/**
 * Returns the current company's API access key (e.g. "UNITYCRM0021").
 * Falls back to the legacy hardcoded value if not stored.
 */
getCurrentCompanyAccessKey(): string {
  const currentCompany = localStorage.getItem('currentCompany') || 'unity';
  return localStorage.getItem(`tibss.${currentCompany}-accessKey`) || 'UNITYCRM0021';
}

/**
 * Returns the pre-computed effective permission entry for a resource.
 *
 * The backend appends a benchmark entry (no id / UserID / GroupID / RoleID)
 * as the last item in resource_permission[].  This represents the resolved
 * permission for the requesting user, taking user → group → role priority
 * into account on the server side.
 *
 * Falls back to the last entry when no explicit benchmark is found.
 */
getEffectivePermission(permissions: any[]): any {
  if (!permissions?.length) return null;
  // Benchmark entry has no identity fields — only permission flags
  return permissions[0];
}
   /**
   * Add or update a resource category in IndexedDB.
   * ✅ Optimized: Uses a **single DB operation** instead of `getByKey()` + `update()/add()`.
   */
   async addResourceCategory(resourceCategory: any): Promise<any> {
    try {
      // Try to update first (if it exists), otherwise add it
      return await firstValueFrom(
        this.dbService.update('resource_categories', resourceCategory)
      ).catch(async (error) => {
        console.warn(`Category not found, adding new: ${resourceCategory.Name}`);
        return await firstValueFrom(this.dbService.add('resource_categories', resourceCategory));
      });
    } catch (error) {
      console.error(`Error saving resource category: ${resourceCategory.Name}`, error);
      throw error;
    }
  }
  /**
   * Fetch a resource category by its ID.
   * Checks the in-memory cache first, then IndexDB.
   */
  getResourceCategoryById(id: string): Promise<any> {
    if (this._categoryCache.has(id)) {
      return Promise.resolve(this._categoryCache.get(id));
    }
    return firstValueFrom(this.dbService.getByKey('resource_categories', id)).then(result => {
      if (result) this._categoryCache.set(id, result);
      return result;
    });
  }

  /**
   * Fetch all resource categories from IndexedDB.
   */
  getAllResourceCategories(): Promise<any[]> {
    return firstValueFrom(this.dbService.getAll('resource_categories'));
  }

  /**
   * Update a resource category in IndexedDB.
   * ✅ Uses `update()` and falls back to `add()` if not found.
   */
  async updateResourceCategory(resourceCategory: any): Promise<any> {
    try {
      return await firstValueFrom(this.dbService.update('resource_categories', resourceCategory));
    } catch (error) {
      console.error(`Error updating resource category: ${resourceCategory.Name}`, error);
      throw error;
    }
  }

  /**
   * Delete a resource category by ID.
   */
  deleteResourceCategory(id: number): Promise<any> {
    return firstValueFrom(this.dbService.delete('resource_categories', id));
  }

  /**
   * Fetch all resource categories from API.
   * ✅ Uses `firstValueFrom()` instead of `toPromise()`.
   */
  async fetchAllResourceCategories(): Promise<any> {
    try {
      const url = 'api/system/resource-permission-detail';
      const body = {
        userID: this.getCurrentUserId(),
        companyId: this.getCurrentCompanyAccessKey(),
      };
      return await firstValueFrom(this.http.get(url, {params:body}));
    } catch (error) {
      console.error('Error fetching resource categories from API:', error);
      throw error;
    }
  }

  /**
   * Get user details from local storage.
   */
  getUser() {
    return this.userDetails;
  }

 /**
 * Ensures that a store exists in IndexedDB.
 * ✅ Optimized: Uses a **single indexedDB.open() call** per execution.
 */
private ensureStoreExists(storeName: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const request = indexedDB.open('CombinedDB', 3); // ✅ Use the correct version

    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, { keyPath: 'Name', autoIncrement: false });
        console.log(`Store '${storeName}' created successfully.`);
      }
    };

    request.onsuccess = () => {
      request.result.close();
      resolve();
    };

    request.onerror = () => {
      console.error(`Error opening database:`, request.error);
      reject(request.error);
    };
  });
}

/**
 * Updates resource permissions from API and stores them in IndexedDB.
 *
 * KEY BEHAVIOUR:
 * 1. Deduplication — if a fetch is already in-flight (e.g. sidebar + report-details
 *    both call this on init), all callers share the same Promise. Only ONE HTTP
 *    request goes out.
 * 2. Session cache — once fetched successfully for this user+company, subsequent
 *    calls within the same page session return immediately (IndexedDB already
 *    populated). Resets on page reload, company switch, or logout.
 */
async updatePermission(): Promise<void> {
  const currentKey = `${this.getCurrentUserId()}__${this.getCurrentCompanyAccessKey()}`;

  // Already fetched for this user/company this session — IndexedDB is warm.
  if (this._sessionKey === currentKey) {
    return;
  }

  // Another caller is already fetching — share that Promise (deduplication).
  if (this._updateInFlight) {
    return this._updateInFlight;
  }

  this._updateInFlight = this._doFetchAndStore(currentKey).finally(() => {
    this._updateInFlight = null;
  });

  return this._updateInFlight;
}

private async _doFetchAndStore(key: string): Promise<void> {
  try {
    const res: any = await this.fetchAllResourceCategories();
    const data = res?.data || [];

    if (data.length) {
      const addOrUpdatePromises = data.map((val: any) => this.addResourceCategory(val));
      await Promise.all(addOrUpdatePromises);
      // Warm the in-memory cache so subsequent getResourceCategoryById calls skip IndexDB.
      data.forEach((val: any) => { if (val?.Name) this._categoryCache.set(val.Name, val); });
      console.log('Resource categories updated successfully.');
    }

    this._sessionKey = key;
  } catch (error) {
    console.error('Error updating permissions:', error);
  }
}

/**
 * Invalidate the in-memory session cache.
 * Call this on company switch or logout so the next updatePermission()
 * re-fetches fresh data for the new company/user.
 * NOTE: company switch uses window.location.href (full reload) so this
 * is mainly needed if a soft company switch is ever added in the future.
 */
invalidatePermissions(): void {
  this._sessionKey = null;
  this._updateInFlight = null;
  this._categoryCache.clear();
}

/**
 * Full permission reset: clears in-memory cache AND wipes the IDB store.
 * Call on logout so the next user never sees stale permissions from a
 * previous session.  Swallows IDB errors so logout is never blocked.
 */
async clearAllPermissions(): Promise<void> {
  this.invalidatePermissions();
  try {
    await firstValueFrom(this.dbService.clear('resource_categories'));
  } catch {
    // Non-fatal — stale permissions are a UX issue, not a security one.
    // The guard's updatePermission() call will overwrite data on next login.
  }
}

}
